/**
 * Registre des contenus "Comprendre cette page".
 *
 * Chaque sujet suit la même trame en quatre volets, pour que le client
 * retrouve la même structure d'une page à l'autre :
 *   1. purpose — en quelques mots
 *   2. howTo   — dans la pratique
 *   3. impacts — ce que ça implique
 *   4. notes   — à retenir
 *
 * Les impacts marqués `irreversible` reçoivent un marqueur discret, réservé
 * aux actions sans retour en arrière.
 *
 * Une entrée peut porter `when` pour n'apparaître que si le module ou
 * l'option correspondante est active sur le compte.
 */

const HOW_IT_WORKS_WHEN = {
  MONITORING: "monitoring",
  SORTING_ERRORS: "sortingErrors",
  ROTATIONS: "rotations",
  CHARACTERIZATION: "characterization",
  PERFORMANCE_ROTATIONS: "performanceRotations",
};

const asEntry = (item) => {
  if (typeof item === "string") {
    return { id: item, when: undefined, irreversible: false };
  }
  return {
    id: item.id,
    when: item.when,
    irreversible: Boolean(item.irreversible),
  };
};

const buildTopic = (name, { purpose, howTo, impacts, notes, links = [] }) => {
  const prefix = `howItWorks.${name}`;
  return {
    name,
    titleKey: `${prefix}.title`,
    purpose: purpose.map((item) => {
      const { id, when } = asEntry(item);
      return { key: `${prefix}.purpose.${id}`, when };
    }),
    howTo: howTo.map((item) => {
      const { id, when } = asEntry(item);
      return { key: `${prefix}.howTo.${id}`, when };
    }),
    impacts: impacts.map((item) => {
      const { id, when, irreversible } = asEntry(item);
      return {
        id,
        labelKey: `${prefix}.impacts.${id}.label`,
        textKey: `${prefix}.impacts.${id}.text`,
        irreversible,
        when,
      };
    }),
    notes: notes.map((item) => {
      const { id, when } = asEntry(item);
      return { key: `${prefix}.notes.${id}`, when };
    }),
    links: links.map((link) => ({
      id: link.id,
      labelKey: link.labelKey,
      href: link.href,
      download: link.download,
      to: link.to,
      when: link.when,
    })),
  };
};

const isWhenVisible = (when, flags = {}) => {
  if (!when) return true;
  switch (when) {
    case HOW_IT_WORKS_WHEN.MONITORING:
      return Boolean(flags.monitoringModuleOn);
    case HOW_IT_WORKS_WHEN.SORTING_ERRORS:
      return Boolean(flags.activatedSortingErrorOption);
    case HOW_IT_WORKS_WHEN.ROTATIONS:
      return Boolean(flags.showRotations);
    case HOW_IT_WORKS_WHEN.CHARACTERIZATION:
      return Boolean(flags.characterizationModuleOn);
    case HOW_IT_WORKS_WHEN.PERFORMANCE_ROTATIONS:
      return Boolean(flags.showRecommendationTypes);
    default:
      return true;
  }
};

export const resolveHowItWorksTopic = (topic, flags = {}) => {
  if (!topic) return null;
  return {
    ...topic,
    purpose: topic.purpose
      .filter((item) => isWhenVisible(item.when, flags))
      .map((item) => item.key),
    howTo: topic.howTo
      .filter((item) => isWhenVisible(item.when, flags))
      .map((item) => item.key),
    impacts: topic.impacts.filter((item) => isWhenVisible(item.when, flags)),
    notes: topic.notes
      .filter((item) => isWhenVisible(item.when, flags))
      .map((item) => item.key),
    links: (topic.links || []).filter((link) =>
      isWhenVisible(link.when, flags),
    ),
  };
};

export const HOW_IT_WORKS_TOPICS = {
  dashboard: buildTopic("dashboard", {
    purpose: [
      "role",
      "period",
      { id: "volumes", when: HOW_IT_WORKS_WHEN.MONITORING },
      { id: "sortingErrors", when: HOW_IT_WORKS_WHEN.SORTING_ERRORS },
      { id: "rotations", when: HOW_IT_WORKS_WHEN.ROTATIONS },
      { id: "characterizations", when: HOW_IT_WORKS_WHEN.CHARACTERIZATION },
    ],
    howTo: [
      "cards",
      { id: "sortingHistogram", when: HOW_IT_WORKS_WHEN.SORTING_ERRORS },
      {
        id: "characterizationCharts",
        when: HOW_IT_WORKS_WHEN.CHARACTERIZATION,
      },
      "details",
    ],
    impacts: [
      { id: "sortingCard", when: HOW_IT_WORKS_WHEN.SORTING_ERRORS },
      { id: "histogram", when: HOW_IT_WORKS_WHEN.SORTING_ERRORS },
      { id: "rotations", when: HOW_IT_WORKS_WHEN.ROTATIONS },
      {
        id: "characterizationReports",
        when: HOW_IT_WORKS_WHEN.CHARACTERIZATION,
      },
      {
        id: "characterizationComposition",
        when: HOW_IT_WORKS_WHEN.CHARACTERIZATION,
      },
      {
        id: "characterizationsInProgress",
        when: HOW_IT_WORKS_WHEN.CHARACTERIZATION,
      },
    ],
    notes: [
      { id: "doNotStack", when: HOW_IT_WORKS_WHEN.SORTING_ERRORS },
      "modules",
    ],
  }),

  settingsHub: buildTopic("settingsHub", {
    purpose: ["role", "impact"],
    howTo: ["chain", "ownership", "support"],
    impacts: ["missingFlow", "silentSensor", "quality"],
    notes: ["whereToStart", "history"],
  }),

  sites: buildTopic("sites", {
    purpose: ["role"],
    howTo: ["declare", "zones"],
    impacts: [
      "openingHours",
      "outlets",
      "rename",
      { id: "delete", irreversible: true },
    ],
    notes: ["openingHoursValue"],
  }),

  wasteOrigins: buildTopic("wasteOrigins", {
    purpose: ["role"],
    howTo: ["create", "importFormat", "attach"],
    impacts: [
      "rename",
      "editDetails",
      { id: "delete", irreversible: true },
      "import",
    ],
    notes: ["importPreview", "multiSite", "templateHeaders"],
    links: [
      {
        id: "excelTemplate",
        labelKey: "howItWorks.wasteOrigins.links.excelTemplate",
        href: "/templates/Template_origines_dechets.xlsx",
        download: "Template_origines_dechets.xlsx",
      },
    ],
  }),

  wasteFlows: buildTopic("wasteFlows", {
    purpose: ["role"],
    howTo: ["assign", "rules"],
    impacts: [
      "name",
      "density",
      "color",
      "valorization",
      "unauthorized",
      { id: "delete", irreversible: true },
    ],
    notes: ["materialsCatalog"],
    links: [
      {
        id: "materialsCatalog",
        labelKey: "howItWorks.wasteFlows.links.materialsCatalog",
        to: "/settings/waste/materials",
      },
    ],
  }),

  zones: buildTopic("zones", {
    purpose: ["role"],
    howTo: ["declare", "capture"],
    impacts: [
      "volume",
      "fillingThreshold",
      "flow",
      "notifications",
      { id: "delete", irreversible: true },
    ],
    notes: ["managers", "history"],
  }),

  sensors: buildTopic("sensors", {
    purpose: ["role"],
    howTo: ["managedByAkanthas", "readOnly"],
    impacts: ["diagnosis"],
    notes: ["contact"],
  }),

  collectionContacts: buildTopic("collectionContacts", {
    purpose: ["role"],
    howTo: ["declare", "linkToZone"],
    impacts: ["rotationRequest", "editEmail"],
    notes: ["unlinkedContact"],
  }),

  users: buildTopic("users", {
    purpose: ["role"],
    howTo: ["create", "quota"],
    impacts: [
      "authorizedSites",
      "notifications",
      "password",
      "testNotifications",
      { id: "delete", irreversible: true },
    ],
    notes: ["quotaMessage", "history"],
  }),

  confrontations: buildTopic("confrontations", {
    purpose: ["role"],
    howTo: ["manualWeighing", "reading", "wmae"],
    impacts: ["readOnly"],
    notes: ["representativeness", "interpretation"],
  }),

  generalSettings: buildTopic("generalSettings", {
    purpose: ["role"],
    howTo: ["scope", "whoCanEdit"],
    impacts: ["alertThreshold", "finesThreshold", "valorizationObjective"],
    notes: ["finesRetroactive"],
  }),

  history: buildTopic("history", {
    purpose: ["role", "filters"],
    howTo: ["menu", "detail", "completeInfo", "tags"],
    impacts: ["completeInfo", "detail", "originFilter"],
    notes: ["threeDots", "completeFirst"],
  }),

  performance: buildTopic("performance", {
    purpose: ["role", "filters"],
    howTo: ["tabs", "charts"],
    impacts: ["rotations", "refresh"],
    notes: ["period", "recommendations"],
    links: [
      {
        id: "recommendationTypes",
        labelKey: "howItWorks.performance.links.recommendationTypes",
        to: "/statistics/recommendations/rotations",
        when: HOW_IT_WORKS_WHEN.PERFORMANCE_ROTATIONS,
      },
    ],
  }),

  performanceQuality: buildTopic("performanceQuality", {
    purpose: ["role", "filters"],
    howTo: ["charts", "campaign"],
    impacts: ["heatmap", "campaign"],
    notes: ["reading", "period"],
  }),

  performanceValorization: buildTopic("performanceValorization", {
    purpose: ["role", "filters"],
    howTo: ["cards", "table"],
    impacts: ["objective", "actions"],
    notes: ["formula", "period"],
    links: [
      {
        id: "recommendationTypes",
        labelKey:
          "howItWorks.performanceValorization.links.recommendationTypes",
        to: "/statistics/recommendations/valorization",
        when: HOW_IT_WORKS_WHEN.PERFORMANCE_ROTATIONS,
      },
    ],
  }),

  performanceCarbon: buildTopic("performanceCarbon", {
    purpose: ["role", "filters"],
    howTo: ["reading", "refresh"],
    impacts: ["settings", "trend"],
    notes: ["estimate", "period"],
  }),
};
